import React, { useEffect, useRef, useState } from 'react';
import Button from '../../forms/Button';
import Input from '../../forms/Input';
import Form from '../forms/FullScreenForm';
import Loading from '../../helpers/Loading';
import logo from '../../../assets/images/logo.png';

const TwoFactorSetupForm = props => {
    const [copied, setCopied] = useState(false);
    const [qrLoaded, setQrLoaded] = useState(false);
    const codeInputRef = useRef(null);
    const CODE_LENGTH = 6;
    const CODE_ERROR_ID = 'ckp-2fa-setup-code-error';
    const qrRef = useRef(null);

    const canSubmit = !!(props.code && props.code.length === 6);
    const cancelLabel = props.cancelLabel || 'Back to Login';

    useEffect(() => {
        setQrLoaded(false);
        if (qrRef.current?.complete) {
            setQrLoaded(true);
        }
    }, [props.qrCode]);

    useEffect(() => {
        if (!props.errorText) return;
        const node = codeInputRef.current && codeInputRef.current.inputRef ? codeInputRef.current.inputRef : null;
        node?.focus?.();
    }, [props.errorText]);

    const handleCopySecret = () => {
        navigator.clipboard.writeText(props.secret);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Form
            onSubmit={props.onSubmit}
            canSubmit={canSubmit && !props.loading}
            header={<img className="ckp-forms-logo" src={logo} alt="Logo" />}
            body={
                <div className="ckp-2fa-setup">
                    <p className="ckp-2fa-description">
                        Scan this QR code with your authenticator app (Google Authenticator, Microsoft Authenticator,
                        Authy, etc.)
                    </p>

                    {props.qrCode ? (
                        <>
                            {!qrLoaded ? (
                                <div className="ckp-2fa-qr-placeholder">
                                    <Loading label="Loading QR code..." size={40} />
                                </div>
                            ) : null}
                            <div className="ckp-2fa-qr-container" style={!qrLoaded ? { display: 'none' } : undefined}>
                                <img
                                    className="ckp-2fa-qr-code"
                                    src={props.qrCode}
                                    alt="2FA QR Code"
                                    ref={qrRef}
                                    onLoad={() => setQrLoaded(true)}
                                    onError={() => setQrLoaded(true)}
                                />
                            </div>
                        </>
                    ) : (
                        <div className="ckp-2fa-qr-placeholder">
                            <Loading label="Loading QR code..." size={40} />
                        </div>
                    )}

                    {props.secret && (
                        <div className="ckp-2fa-secret-section">
                            <p className="ckp-2fa-secret-label">Can't scan? Enter this code manually:</p>
                            <div className="ckp-2fa-secret-container">
                                <code className="ckp-2fa-secret">{props.secret}</code>
                                <Button
                                    type="button"
                                    className={`ckp-2fa-copy-button ${copied ? 'is-copied' : ''}`}
                                    onClick={handleCopySecret}
                                    label={copied ? 'COPIED' : 'COPY'}
                                />
                            </div>
                        </div>
                    )}

                    <div className="ckp-2fa-code-input">
                        <Input
                            label="Enter your 2FA code"
                            name="code"
                            type="text"
                            value={props.code}
                            onChange={props.onInput}
                            ref={codeInputRef}
                            errorMessage={props.errorText || ''}
                            helperText={
                                props.errorText ? (
                                    <span id={CODE_ERROR_ID} role="alert" aria-live="assertive">
                                        {props.errorText}
                                    </span>
                                ) : (
                                    ''
                                )
                            }
                            inputProps={{
                                maxLength: CODE_LENGTH,
                                pattern: '[0-9]*',
                                inputMode: 'numeric',
                                autoComplete: 'one-time-code',
                                'aria-invalid': !!props.errorText,
                                'aria-describedby': props.errorText ? CODE_ERROR_ID : undefined
                            }}
                        />
                    </div>
                </div>
            }
            footer={
                <div className="ckp-2fa-footer">
                    <Button loading={props.loading} inactive={!canSubmit} label="Verify and Enable" />
                    <button type="button" className="ckp-2fa-cancel-link" onClick={props.onCancel}>
                        {cancelLabel}
                    </button>
                </div>
            }
        />
    );
};

export default TwoFactorSetupForm;
